import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import event_buffer from "../../context/event_def";
import { DocumentData } from '../../context/DocumentDataProvider';
import { TabNode } from 'flexlayout-react'; // Import TabNode to handle tab activation
import * as Menubar from '@radix-ui/react-menubar';
import { ChevronRightIcon, DotFilledIcon } from "@radix-ui/react-icons";

interface PlotlyTabProps {
    plotIndex: string;
    plots: Map<string, any>;
    setPlots: React.Dispatch<React.SetStateAction<Map<string, any>>>;
    node?: TabNode; // Add optional node prop to handle tab events
}

const PlotlyTab: React.FC<PlotlyTabProps> = ({ plotIndex, plots, setPlots, node }) => {
    const plotRef = useRef<HTMLDivElement>(null);
    const [plotId, setPlotId] = useState<string>(plotIndex);
    // axisScale holds the current axis scale types for the visible plot
    // keys: 'x' and 'y' values: 'linear' | 'log'
    const [axisScale, setAxisScale] = useState<{ x: 'linear' | 'log'; y: 'linear' | 'log' }>({ x: 'linear', y: 'linear' });

    // normalize layout so that empty titles do not reserve space and margins are minimal
    const normalizeLayout = (layout?: any) => {
        const l = Object.assign({}, layout || {});

        // detect if a meaningful title exists
        const hasTitle = !!(l.title && ((typeof l.title === 'string' && l.title.trim() !== '') || (l.title && typeof l.title.text === 'string' && l.title.text.trim() !== '')));
        if (!hasTitle) {
            // remove title entirely so Plotly doesn't reserve space for it
            delete l.title;
        }

    // prefer autosize and reasonable margins so plot takes maximum space
    // but leaves room for axis tick numbers (avoids clipping)
    l.autosize = true;
    // sensible defaults: left and bottom must allow tick labels to appear
    const defaultMargin = { l: 50, r: 10, t: hasTitle ? 24 : 6, b: 40, pad: 0 };
    l.margin = Object.assign({}, defaultMargin, l.margin || {});

    // Ensure axis tick labels are shown unless explicitly disabled in incoming layout
    l.xaxis = Object.assign({ showticklabels: true }, l.xaxis || {});
    l.yaxis = Object.assign({ showticklabels: true }, l.yaxis || {});

        return l;
    };

    const primary = '#8d3939ff';

    // Ensure each trace uses the app primary color for lines/markers unless explicitly set
    const applyPrimaryColorToTraces = (data: any) => {
        if (!data) return data;
        try {
            const traces = Array.isArray(data) ? data : [data];
            return traces.map((t: any) => {
                const trace = Object.assign({}, t);
                // apply line color for scatter / lines traces if not present
                if (!trace.line) trace.line = {};
                if (!('color' in trace.line) || !trace.line.color) {
                    // only apply for traces that can have lines
                    trace.line = Object.assign({}, trace.line, { color: primary });
                }
                // apply marker color if markers exist and color not set
                if (trace.marker === undefined) trace.marker = {};
                if (!('color' in trace.marker) || !trace.marker.color) {
                    trace.marker = Object.assign({}, trace.marker, { color: primary });
                }
                return trace;
            });
        } catch (e) {
            return data;
        }
    };

    useEffect(() => {
        console.log("PlotlyTab mounted with plotId:", plotId);
        const plotData = plots.get(plotId);
        if (plotData) {
            if (plotData.data && plotData.layout) {
                const layout = normalizeLayout(plotData.layout);
                const dataWithColor = applyPrimaryColorToTraces(plotData.data);
                Plotly.react(plotRef.current, dataWithColor, layout, plotData.config || {});
            } else {
                // plotData might already be a figure-like object or raw traces
                const maybeData = plotData.data ? plotData.data : plotData;
                const layout = normalizeLayout(plotData.layout);
                const dataWithColor = applyPrimaryColorToTraces(maybeData);
                Plotly.react(plotRef.current, dataWithColor, layout, plotData.config || {});
            }
        }
    }, [plots]);

    // Update internal axisScale state from the plot layout (when plots change)
    useEffect(() => {
        const plotData = plots.get(plotId);
        if (!plotData) return;
        const layout = plotData.layout || {};
        const xType = layout.xaxis && layout.xaxis.type ? layout.xaxis.type : 'linear';
        const yType = layout.yaxis && layout.yaxis.type ? layout.yaxis.type : 'linear';
        setAxisScale({ x: xType === 'log' ? 'log' : 'linear', y: yType === 'log' ? 'log' : 'linear' });
    }, [plots, plotId]);

    // Helper: set axis scale and update plot
    const setAxisType = (axis: 'x' | 'y', type: 'linear' | 'log') => {
        const plotEl = plotRef.current;
        const plotData = plots.get(plotId);
        if (!plotData || !plotEl) return;

        // ensure layout exists
        const newLayout = Object.assign({}, plotData.layout || {});
        if (axis === 'x') {
            newLayout.xaxis = Object.assign({}, newLayout.xaxis || {}, { type: type === 'log' ? 'log' : 'linear' });
        } else {
            newLayout.yaxis = Object.assign({}, newLayout.yaxis || {}, { type: type === 'log' ? 'log' : 'linear' });
        }

        // update map copy and state
        const newPlots = new Map(plots);
        const updated = Object.assign({}, plotData, { layout: newLayout });
        newPlots.set(plotId, updated);
        setPlots(newPlots);

        // apply relayout to Plotly
        try {
            const relayoutObj: any = {};
            if (axis === 'x') relayoutObj['xaxis.type'] = type === 'log' ? 'log' : 'linear';
            else relayoutObj['yaxis.type'] = type === 'log' ? 'log' : 'linear';
            Plotly.relayout(plotEl, relayoutObj);
        } catch (e) {
            console.warn('Plotly relayout error:', e);
        }

        setAxisScale(prev => ({ ...prev, [axis]: type }));
    };

    // Export helpers
    const download = (content: string, filename: string, mime = 'text/plain') => {
        const blob = new Blob([content], { type: mime + ';charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportCSV = () => {
        const plotData = plots.get(plotId);
        if (!plotData || !plotData.data) return;
        const traces = plotData.data as any[];

        // Build columns combining traces. We'll produce columns traceN_x, traceN_y
        const cols: string[] = [];
        const arrays: any[][] = [];
        traces.forEach((t, i) => {
            const xs = Array.isArray(t.x) ? t.x : [];
            const ys = Array.isArray(t.y) ? t.y : [];
            cols.push(`trace${i + 1}_x`, `trace${i + 1}_y`);
            arrays.push(xs, ys);
        });

        const maxLen = arrays.reduce((m, arr) => Math.max(m, arr.length), 0);
        const rows: string[] = [];
        rows.push(cols.join(','));
        for (let r = 0; r < maxLen; r++) {
            const row: string[] = [];
            for (let c = 0; c < arrays.length; c++) {
                const v = arrays[c][r];
                row.push(v === undefined || v === null ? '' : String(v));
            }
            rows.push(row.join(','));
        }

        download(rows.join('\n'), `${plotId || 'plot'}.csv`, 'text/csv');
    };

    const exportCode = (format: 'python' | 'matlab') => {
        const plotData = plots.get(plotId);
        if (!plotData || !plotData.data) return;
        const traces = plotData.data as any[];
        const layout = plotData.layout || {};

        const getAxisTitle = (ax: any, fallback: string) => {
            if (!ax) return fallback;
            if (typeof ax.title === 'string') return ax.title;
            if (ax.title && typeof ax.title.text === 'string') return ax.title.text;
            return fallback;
        };

        const xTitle = getAxisTitle(layout.xaxis, 'x');
        const yTitle = getAxisTitle(layout.yaxis, 'y');
        const xType = (layout.xaxis && layout.xaxis.type) || axisScale.x || 'linear';
        const yType = (layout.yaxis && layout.yaxis.type) || axisScale.y || 'linear';

        if (format === 'python') {
            // Use matplotlib for Python export
            let code = "import matplotlib.pyplot as plt\n\n";

            // helper to convert JSON-style arrays to Python literals (None for null)
            const toPy = (arr: any[]) => {
                try {
                    return JSON.stringify(arr)
                        .replace(/\bnull\b/g, 'None')
                        .replace(/\btrue\b/g, 'True')
                        .replace(/\bfalse\b/g, 'False');
                } catch (e) {
                    return '[]';
                }
            };

            traces.forEach((t, i) => {
                code += `x${i + 1} = ${toPy(t.x || [])}\n`;
                code += `y${i + 1} = ${toPy(t.y || [])}\n`;
            });

            code += '\nfig, ax = plt.subplots()\n';
            traces.forEach((t, i) => {
                const name = t.name ? String(t.name).replace(/'/g, "\\'") : `trace${i + 1}`;
                code += `ax.plot(x${i + 1}, y${i + 1}, label='${name}')\n`;
            });

            if (xType === 'log') code += "ax.set_xscale('log')\n";
            if (yType === 'log') code += "ax.set_yscale('log')\n";

            code += `ax.set_xlabel(${JSON.stringify(xTitle)})\n`;
            code += `ax.set_ylabel(${JSON.stringify(yTitle)})\n`;
            code += 'ax.legend()\nplt.show()\n';

            download(code, `${plotId || 'plot'}.py`, 'text/x-python');
        } else {
            // Matlab export: include axis labels and axis scale
            let code = '';
            traces.forEach((t, i) => {
                const xs = JSON.stringify(t.x || []).replace(/\[/g, '[').replace(/\]/g, ']').replace(/,/g, ' ');
                const ys = JSON.stringify(t.y || []).replace(/\[/g, '[').replace(/\]/g, ']').replace(/,/g, ' ');
                code += `x${i + 1} = ${xs};\n`;
                code += `y${i + 1} = ${ys};\n`;
            });

            code += '\nfigure; hold on;\n';
            traces.forEach((t, i) => {
                const name = t.name ? String(t.name).replace(/'/g, "'\''") : `trace${i + 1}`;
                // Matlab legend labels are set via legend() later; plot without name
                code += `plot(x${i + 1}, y${i + 1});\n`;
            });

            // set axis scales
            if (xType === 'log') code += `set(gca, 'XScale', 'log');\n`;
            if (yType === 'log') code += `set(gca, 'YScale', 'log');\n`;

            // labels
            code += `xlabel(${JSON.stringify(xTitle)});\n`;
            code += `ylabel(${JSON.stringify(yTitle)});\n`;

            // add legend with series names if available
            const names = traces.map((t, i) => (t.name ? String(t.name) : `trace${i + 1}`));
            if (names.length) {
                const matlabNames = names.map(n => `'${n.replace(/'/g, "'\\''")}'`).join(', ');
                code += `legend(${matlabNames});\n`;
            }

            code += 'hold off;\n';
            download(code, `${plotId || 'plot'}.m`, 'text/x-matlab');
        }
    };

    useEffect(() => {
        const el = plotRef.current;
        const pd = plots.get(plotId);
        if (!el || !pd) return;

        const data = pd.data ? pd.data : pd;
        const layout = normalizeLayout(pd.layout);
        try {
            const dataWithColor = applyPrimaryColorToTraces(data);
            Plotly.newPlot(el, dataWithColor, layout, pd.config || {});
        } catch (e) {
            console.warn('Plotly newPlot error:', e);
        }
        if (!el || !(window as any).ResizeObserver || !(window as any).MutationObserver) return;

        const handleResize = () => {
            if (el.offsetParent !== null) {
                try {
                    Plotly.Plots.resize(el);
                } catch (e) {
                    console.warn('Plotly resize error:', e);
                }
            }
        };

        const containerEl = el.closest('.lm_item');
        const resizeObserver = new (window as any).ResizeObserver(handleResize);
        if (containerEl) resizeObserver.observe(containerEl);

        const mutationObserver = new (window as any).MutationObserver(() => {
            if (el.offsetParent !== null) {
                setTimeout(() => {
                    try {
                        Plotly.Plots.resize(el);
                    } catch (e) {}
                }, 0);
            }
        });
        mutationObserver.observe(el, { attributes: true, attributeFilter: ['style', 'class'] });

        // Listen for tab activation
        const handleTabActivation = () => {
            setTimeout(() => {
                try {
                    Plotly.Plots.resize(el);
                } catch (e) {
                    console.warn('Plotly resize error on tab activation:', e);
                }
            }, 0);
        };

        if (node) {
            node.setEventListener('resize', handleTabActivation);
        }

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            if (node) {
                node.setEventListener('resize', () => {}); // Provide an empty function to clear the listener
            }
        };
    }, [plots, node]);

    return (
        <>
            <Menubar.Root className="menubar" style={{ position: 'relative', zIndex: 10 , left: 5, top: 5}}>
                {/* Axis menu */}
                <Menubar.Menu>
                    <Menubar.Trigger className="menubar-trigger">Axis</Menubar.Trigger>
                    <Menubar.Content className="menubar-content" sideOffset={1} align="start" style={{ zIndex: 10 }}>
                        <div style={{ padding: '6px 8px' }}>
                            <div style={{ marginBottom: 6 }}><strong>X Axis</strong></div>
                            <Menubar.RadioGroup value={axisScale.x} onValueChange={(v) => setAxisType('x', v as 'linear' | 'log')}>
                                <Menubar.RadioItem className="menubar-item" value="linear">
                                    <Menubar.ItemIndicator className="menubarItemIndicator">
                                        <DotFilledIcon />
                                    </Menubar.ItemIndicator>
                                    Linear
                                </Menubar.RadioItem>
                                <Menubar.RadioItem className="menubar-item" value="log">
                                    <Menubar.ItemIndicator className="menubarItemIndicator">
                                        <DotFilledIcon />
                                    </Menubar.ItemIndicator>
                                    Logarithmic
                                </Menubar.RadioItem>
                            </Menubar.RadioGroup>
                            <div style={{ margin: '8px 0' }} />
                            <div style={{ marginBottom: 6 }}><strong>Y Axis</strong></div>
                            <Menubar.RadioGroup value={axisScale.y} onValueChange={(v) => setAxisType('y', v as 'linear' | 'log')}>
                                <Menubar.RadioItem className="menubar-item" value="linear">
                                    <Menubar.ItemIndicator className="menubarItemIndicator">
                                        <DotFilledIcon />
                                    </Menubar.ItemIndicator>
                                    Linear
                                </Menubar.RadioItem>
                                <Menubar.RadioItem className="menubar-item" value="log">
                                    <Menubar.ItemIndicator className="menubarItemIndicator">
                                        <DotFilledIcon />
                                    </Menubar.ItemIndicator>
                                    Logarithmic
                                </Menubar.RadioItem>
                            </Menubar.RadioGroup>
                        </div>
                    </Menubar.Content>
                </Menubar.Menu>

                {/* Export menu */}
                <Menubar.Menu>
                    <Menubar.Trigger className="menubar-trigger">Export</Menubar.Trigger>
                    <Menubar.Content className="menubar-content" sideOffset={1} align="start" style={{ zIndex: 10 }}>
                        <Menubar.Item className="menubar-item" onSelect={exportCSV}>CSV</Menubar.Item>
                        <Menubar.Item className="menubar-item" onSelect={() => exportCode('python')}>Python</Menubar.Item>
                        <Menubar.Item className="menubar-item" onSelect={() => exportCode('matlab')}>Matlab</Menubar.Item>
                    </Menubar.Content>
                </Menubar.Menu>
            </Menubar.Root>

            <div ref={plotRef} style={{ width: '100%', height: 'calc(100% - 36px)', position: 'relative', zIndex: 0 }} />
        </>
    );
};

export default PlotlyTab;